import { RadioButton, RadioButtonTrueFalse } from "Components/Form/RadioButton";
import {
  formatNumber,
  formatNumberStringToNumber,
  removeWhitespace,
} from "Formatting/index";
import {
  AdvicePriceFormValues,
  OneTimePaymentAdvisorChoice,
} from "../interfaces";
import { OneTimePaymentWrapper, UwInlineAlert } from "../styled";
import { AdviceRestrictions, getOneTimePaymentTextDeprecated } from "../utils";
import {
  getDisabilityPaymentAfter3MonthsHint,
  hasExceededMaxValueForAuthorizedAdvisor,
  hasExceededMaxValueForNormalAdvisor,
  isWithinValidOtpRange,
} from "./utils";

import { StyledRow } from "App/global-style";
import { Input } from "Components/Form/Input";
import { InputMode } from "Components/Form/Input/inputTypes";
import { VALUE_TYPE } from "Components/NumberValue";
import { ThemedSpinner } from "Components/Themes/components/ThemedSpinner";
import { AdviceProductChangeType } from "Store/advice/interfaces";
import { formBooleanToBoolean } from "Utils/formUtils";
import { isDefined } from "Utils/functions";
import { getError } from "Utils/productUtils";
import { useFormikContext } from "formik";
import React from "react";
import { AdviceProductDetailsFormValues } from "../../../ProductDetails/FormElements/interfaces";

let timer: ReturnType<typeof setTimeout> | undefined;
const debounce = (f: () => any): void => {
  if (isDefined(timer)) {
    clearTimeout(timer);
  }
  timer = setTimeout(() => {
    f();
    timer = undefined;
  }, 1000);
};

interface Props {
  adviceProductIntroFormValues: AdviceProductDetailsFormValues;
  oneTimePaymentValue: number;
  oneTimePayment: any;
  lang: string;
  handleCustomerChange: (
    changeType: AdviceProductChangeType,
    changeValue: number | boolean,
  ) => void;
  priceIsLoading: boolean;
  adviceRestrictions: AdviceRestrictions;
  eligibleForPaymentAfter3Months?: boolean;
  customerOver50AndCanNotHaveOTP: boolean;
}
export const DisabilityOneTimePaymentAdvisor = ({
  adviceProductIntroFormValues,
  oneTimePaymentValue,
  oneTimePayment,
  lang,
  handleCustomerChange,
  priceIsLoading,
  adviceRestrictions,
  eligibleForPaymentAfter3Months,
  customerOver50AndCanNotHaveOTP,
}: Props): React.ReactElement => {
  const form = useFormikContext<AdvicePriceFormValues>();
  const { values } = form;
  const advisorChoice = values.disabilityOneTimePaymentAdvisorChoice;
  const advisorValue = values.disabilityOneTimePaymentAdvisorValue;
  const advisorValueNumber = advisorValue
    ? Number(removeWhitespace(advisorValue))
    : 0;
  const twoTimesIncome = adviceProductIntroFormValues.income * 2;

  const handleOneTimePaymentAdvisorChoice = (choice: string): void => {
    if (choice === OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT) {
      handleCustomerChange(
        AdviceProductChangeType.ADVISOR_ADVICED_ONE_TIME_PAYMENT,
        oneTimePaymentValue,
      );
      form.setFieldValue("disabilityOneTimePaymentValue", oneTimePaymentValue);
    }
    if (choice === OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT) {
      handleCustomerChange(
        AdviceProductChangeType.ADVISOR_NO_ONE_TIME_PAYMENT,
        0,
      );
      form.setFieldValue("disabilityOneTimePaymentValue", 0);
    }
  };

  const handleCustomOneTimePaymentAdvisor = (
    value: string,
    restrictions: AdviceRestrictions,
  ): void => {
    const valueAsNumber = formatNumberStringToNumber(value);
    if (
      advisorChoice === OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT &&
      valueAsNumber > 0
    ) {
      if (isWithinValidOtpRange(twoTimesIncome, valueAsNumber, restrictions)) {
        handleCustomerChange(
          AdviceProductChangeType.ADVISOR_CUSTOM_ONE_TIME_PAYMENT,
          valueAsNumber,
        );
      } else {
        form.setFieldTouched("disabilityOneTimePaymentAdvisorValue", true);
      }
    }
  };

  return (
    <>
      {eligibleForPaymentAfter3Months && (
        <RadioButtonTrueFalse
          name="disabilityPaymentAfter3Months"
          id="disabilityPaymentAfter3Months"
          label={oneTimePayment.advisorTexts.waitingPeriod.label[lang]}
          hint={getDisabilityPaymentAfter3MonthsHint(
            eligibleForPaymentAfter3Months,
            adviceProductIntroFormValues,
            oneTimePayment,
            lang,
          )}
          options={[
            {
              label:
                oneTimePayment.advisorTexts.waitingPeriod.paymentAfter12Months[
                  lang
                ],
              value: false,
              id: "12Months",
            },
            {
              label:
                oneTimePayment.advisorTexts.waitingPeriod.paymentAfter3Months[
                  lang
                ],
              value: true,
              id: "3Months",
            },
          ]}
          direction="vertical"
          error={getError("disabilityPaymentAfter3Months", form)}
          onChange={(e) => {
            handleCustomerChange(
              AdviceProductChangeType.ADVISOR_PAYMENT_AFTER_3_MONTHS,
              formBooleanToBoolean(e?.target.value),
            );
          }}
        />
      )}
      {!customerOver50AndCanNotHaveOTP && (
        <OneTimePaymentWrapper>
          <StyledRow className="OneTimePaymentRadio">
            <RadioButton
              name="disabilityOneTimePaymentAdvisorChoice"
              id="disabilityOneTimePaymentAdvisorChoice"
              label={oneTimePayment.header[lang]}
              options={[
                ...(oneTimePaymentValue > 0
                  ? [
                      {
                        label: getOneTimePaymentTextDeprecated(
                          adviceProductIntroFormValues,
                          oneTimePaymentValue,
                          oneTimePayment,
                          lang,
                        ),
                        value:
                          OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT,
                        id: `disabilityOneTimePaymentAdvisorChoice-${OneTimePaymentAdvisorChoice.ADVICED_ONE_TIME_PAYMENT}`,
                      },
                    ]
                  : []),
                {
                  label:
                    oneTimePayment.advisorTexts.customOneTimePaymentAdvisor[
                      lang
                    ],
                  value: OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT,
                  id: `disabilityOneTimePaymentAdvisorChoice-${OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT}`,
                },
                {
                  label:
                    oneTimePayment.advisorTexts.noOneTimePaymentAdvisor[lang],
                  value: OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT,
                  id: `disabilityOneTimePaymentAdvisorChoice-${OneTimePaymentAdvisorChoice.NO_ONE_TIME_PAYMENT}`,
                },
              ]}
              direction="vertical"
              error={getError("disabilityOneTimePaymentAdvisorChoice", form)}
              onChange={(e) => {
                const choice = e.target.value;
                handleOneTimePaymentAdvisorChoice(choice);
              }}
            />
          </StyledRow>
          {advisorChoice ===
            OneTimePaymentAdvisorChoice.CUSTOM_ONE_TIME_PAYMENT && (
            <StyledRow>
              <Input
                large
                id="disabilityOneTimePaymentAdvisorValue"
                name="disabilityOneTimePaymentAdvisorValue"
                label={
                  oneTimePayment.advisorTexts.customOneTimePaymentAdvisorLabel[
                    lang
                  ]
                }
                suffix={VALUE_TYPE.NOK}
                error={getError("disabilityOneTimePaymentAdvisorValue", form)}
                type="string"
                onChange={(e) => {
                  e.target.value = formatNumber(e.target.value, false);
                  debounce(() =>
                    handleCustomOneTimePaymentAdvisor(
                      e.target.value,
                      adviceRestrictions,
                    ),
                  );
                }}
                disabled={priceIsLoading}
                inputMode={InputMode.TEXT}
                inputProps={{ autoComplete: "off" }}
              />
              {priceIsLoading && <ThemedSpinner black />}
            </StyledRow>
          )}
          {hasExceededMaxValueForNormalAdvisor(
            oneTimePaymentValue,
            advisorValueNumber,
            adviceRestrictions,
            advisorChoice,
          ) && (
            <StyledRow>
              <UwInlineAlert
                title={oneTimePayment.advisorTexts.uwMaxAdvisor[lang]}
              />
            </StyledRow>
          )}
          {hasExceededMaxValueForAuthorizedAdvisor(
            oneTimePaymentValue,
            advisorValueNumber,
            adviceRestrictions,
            advisorChoice,
          ) && (
            <>
              <StyledRow>
                <UwInlineAlert
                  title={oneTimePayment.advisorTexts.uwMaxAuthAdvisor[lang]}
                />
              </StyledRow>
              <StyledRow>
                <UwInlineAlert
                  type="warning"
                  title={
                    oneTimePayment.advisorTexts.uwMaxAuthAdvisorWarning[lang]
                  }
                />
              </StyledRow>
            </>
          )}
        </OneTimePaymentWrapper>
      )}
    </>
  );
};
